import type { PlatformUser, StaffMember } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  Card,
  Checkbox,
  type Column,
  DataTable,
  PageHeader,
  StatusBadge,
  useToast,
} from '@fruuit/ui'
import { Settings2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { adminApi, adminKeys, usePlatformUsers, useStaff } from '../../features/admin-users/api'
import { InviteDialog } from '../../features/admin-users/InviteDialog'
import { formatDateTime, ROLE_LABELS } from '../../features/auth/format'
import { MfaRequiredNotice, useStepUp } from '../../features/auth/StepUp'
import { useTitle } from '../../lib/useTitle'

/** Admin → Staff (P-ADM-008/009): Fruuit manager/reviewer roles and platform users. */
export function StaffPage() {
  useTitle('Staff')
  const me = useSession()
  const operator = me.platform_role === 'platform_operator'
  const staff = useStaff()
  const platform = usePlatformUsers()
  const { toast } = useToast()
  const { guard, dialog } = useStepUp()
  const [inviteOpen, setInviteOpen] = useState(false)

  const setRoles = useApiMutation(
    (vars: { userId: string; manager: boolean; reviewer: boolean }, ctx) =>
      guard(() => adminApi.setStaffRoles(vars, ctx)),
    {
      invalidate: [adminKeys.staff, adminKeys.users],
      onSuccess: () => toast({ tone: 'success', message: 'Staff roles updated.' }),
      onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
    },
  )
  const setOperator = useApiMutation(
    (vars: { userId: string; operator: boolean }, ctx) =>
      guard(() => adminApi.setOperator(vars, ctx)),
    {
      invalidate: [adminKeys.platformUsers, adminKeys.users],
      onSuccess: (_d, vars) =>
        toast({
          tone: 'success',
          message: vars.operator ? 'Operator role granted.' : 'Operator role removed.',
        }),
      onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
    },
  )

  const staffColumns: Column<StaffMember>[] = [
    {
      id: 'name',
      header: 'Person',
      cell: (s) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">
            {s.display_name}
            {s.platform_role ? (
              <Badge tone="brand" className="ml-2 align-middle">
                {ROLE_LABELS[s.platform_role]}
              </Badge>
            ) : null}
          </p>
          <p className="truncate text-xs text-sage">{s.email}</p>
        </div>
      ),
    },
    { id: 'status', header: 'Status', cell: (s) => <StatusBadge status={s.status} /> },
    ...(['manager', 'reviewer'] as const).map(
      (role): Column<StaffMember> => ({
        id: role,
        header: ROLE_LABELS[role],
        cell: (s) => (
          <Checkbox
            label={<span className="sr-only">{`${ROLE_LABELS[role]}: ${s.display_name}`}</span>}
            checked={s[role]}
            disabled={setRoles.isPending || s.status === 'deactivated'}
            onChange={(event) =>
              setRoles.mutate({
                userId: s.id,
                manager: role === 'manager' ? event.target.checked : s.manager,
                reviewer: role === 'reviewer' ? event.target.checked : s.reviewer,
              })
            }
          />
        ),
      }),
    ),
    {
      id: 'last',
      header: 'Last sign-in',
      cell: (s) => (
        <span className="whitespace-nowrap text-sage">{formatDateTime(s.last_sign_in_at)}</span>
      ),
    },
  ]

  const platformColumns: Column<PlatformUser>[] = [
    {
      id: 'name',
      header: 'Platform user',
      cell: (p) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{p.display_name}</p>
          <p className="truncate text-xs text-sage">{p.email}</p>
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      cell: (p) => (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={p.platform_role === 'platform_operator' ? 'brand' : 'outline'}>
            {ROLE_LABELS[p.platform_role]}
          </Badge>
          {p.configured_operator ? <Badge tone="info">Set by configuration</Badge> : null}
        </div>
      ),
    },
    {
      id: 'mfa',
      header: '2FA',
      cell: (p) => (p.mfa_enrolled ? <Badge tone="positive">On</Badge> : 'Off'),
    },
    {
      id: 'last',
      header: 'Last sign-in',
      cell: (p) => (
        <span className="whitespace-nowrap text-sage">{formatDateTime(p.last_sign_in_at)}</span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (p) => {
        if (!operator || p.configured_operator || p.id === me.user.id) return null
        const grant = p.platform_role !== 'platform_operator'
        return (
          <Button
            size="sm"
            variant={grant ? 'secondary' : 'ghost'}
            icon={<Settings2 />}
            loading={setOperator.isPending && setOperator.variables?.vars.userId === p.id}
            onClick={() => setOperator.mutate({ userId: p.id, operator: grant })}
          >
            {grant ? 'Make operator' : 'Remove operator'}
          </Button>
        )
      },
    },
  ]

  const mfaGate = (error: unknown, refetch: () => unknown) =>
    isApiError(error, 'mfa_required') ? (
      <MfaRequiredNotice onVerified={() => void refetch()} />
    ) : (
      <Callout tone="danger" role="alert">
        {errorMessage(error)}
      </Callout>
    )

  return (
    <>
      <PageHeader
        eyebrow="Fruuit staff"
        title="Managers, reviewers and platform users."
        description="Staff roles decide who authors and who independently reviews. Platform users come from the admission rule: verified @fruuitconsulting.com identities and the configured emails."
        actions={
          staff.data?.org_id ? (
            <Button icon={<UserPlus />} onClick={() => setInviteOpen(true)}>
              Invite staff
            </Button>
          ) : null
        }
      />
      <div className="flex flex-col gap-8">
        <section aria-labelledby="staff-heading">
          <h2 id="staff-heading" className="mb-3 font-display text-lg font-semibold text-ink">
            Workspace roles
          </h2>
          {staff.isError ? (
            mfaGate(staff.error, staff.refetch)
          ) : (
            <DataTable
              caption="Fruuit staff"
              columns={staffColumns}
              rows={staff.data?.items ?? []}
              getRowId={(s) => s.id}
              loading={staff.isPending}
              empty={<p className="py-8 text-center text-sm text-sage">No Fruuit staff yet.</p>}
            />
          )}
        </section>

        <Card
          title="Platform users"
          eyebrow="Admin panel access"
          actions={
            operator ? null : (
              <span className="text-xs text-sage">Operators can change roles ⚙</span>
            )
          }
        >
          {platform.isError ? (
            mfaGate(platform.error, platform.refetch)
          ) : (
            <DataTable
              caption="Platform users"
              columns={platformColumns}
              rows={platform.data?.items ?? []}
              getRowId={(p) => p.id}
              loading={platform.isPending}
              empty={<p className="py-8 text-center text-sm text-sage">No platform users.</p>}
            />
          )}
          <p className="mt-4 text-xs leading-relaxed text-sage">
            Operators can also impersonate users, run feeds and grant this role. The admission rule
            is checked at every sign-in, so someone who leaves the Fruuit tenant loses access at
            their next sign-in.
          </p>
        </Card>
      </div>
      {staff.data?.org_id ? (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          orgId={staff.data.org_id}
          lockOrg
          title="Invite Fruuit staff"
        />
      ) : null}
      {dialog}
    </>
  )
}
